import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Info } from 'lucide-react';

import { Button } from '@epoch-ui/components/Button';
import { Checkbox } from '@epoch-ui/components/Checkbox/checkbox';
import { Form, SwitchField, InputField, CheckboxField, Label } from '@epoch-ui/components/Form';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { PlusOneConfig } from 'types/plus-one.types';
import { ErrorMessage } from '@epoch-ui/components/ErrorMessage';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { EVENT_EDITOR_V2 } from 'constants/featureFlags';

import { getPlusOneConfigFormSchema, type PlusOneConfigFormValues } from './plusOneConfig.schema';

export interface PlusOneConfigModalProps extends React.ComponentPropsWithoutRef<typeof Modal> {
  initialValues: Partial<PlusOneConfig>;
  onSubmit: (values: PlusOneConfigFormValues) => void;
  sessionId?: number | string;
  isPublished?: boolean;
  hasRegisteredGuests?: boolean;
  hasRequiredPreEventQuestions?: boolean;
  /** Legacy flow only — remove when cleaning up the event_editor_v2 feature flag */
  wasAllowPlusOnesEnabled?: boolean;
}

export const PlusOneConfigModal = React.memo<PlusOneConfigModalProps>(
  ({
    open,
    onOpenChange,
    initialValues,
    onSubmit,
    sessionId,
    isPublished = false,
    hasRegisteredGuests = false,
    hasRequiredPreEventQuestions = false,
    wasAllowPlusOnesEnabled,
    ...props
  }) => {
    const isEventEditorV2Enabled = useFeatureFlag(EVENT_EDITOR_V2);

    const previouslyEnabled = wasAllowPlusOnesEnabled ?? initialValues.allowPlusOnes;
    const showNotificationSettings = !previouslyEnabled && hasRegisteredGuests;

    const form = useForm<PlusOneConfigFormValues>({
      resolver: zodResolver(getPlusOneConfigFormSchema(initialValues.maxRegisteredPlusOnes)),
      values: {
        maxPlusOnes: 1,
        collectQuestionnaireResponses: false,
        ...initialValues,
        ...(showNotificationSettings && {
          sendEmailNotification: true,
          sendSlackNotification: true
        })
      },
      mode: 'onChange'
    });

    const allowPlusOnes = form.watch('allowPlusOnes');
    const { dirtyFields } = form.formState;

    return (
      <Modal open={open} onOpenChange={onOpenChange} {...props}>
        <Form {...form}>
          <ModalContent>
            <form
              onSubmit={(e) => {
                e.stopPropagation();
                form.handleSubmit((data) => {
                  onSubmit(data);
                  onOpenChange?.(false);
                })(e);
              }}
            >
              <ModalHeader>
                <ModalTitle>Add +1 guests</ModalTitle>
                <ModalDescription>
                  You can allow guests to add plus one guests when they register for the{' '}
                  {sessionId ? 'session' : 'event'}.{' '}
                  <a
                    href="https://help.epochapp.com/articles/plus-one-guests"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tw-text-primary-blue tw-underline"
                  >
                    Learn more
                  </a>
                </ModalDescription>
              </ModalHeader>

              <div className="tw-mt-5 tw-flex tw-flex-col tw-gap-5">
                {(!sessionId || isEventEditorV2Enabled) && (
                  <div>
                    <SwitchField
                      name="allowPlusOnes"
                      label="Allow plus one guests"
                      className="tw-flex-row-reverse tw-justify-between"
                    />
                    {!allowPlusOnes && !!initialValues.maxRegisteredPlusOnes && (
                      <ErrorMessage>
                        There are registered plus one guests. This will remove all plus one guests
                        for this event.
                      </ErrorMessage>
                    )}
                  </div>
                )}

                {allowPlusOnes && (
                  <>
                    {showNotificationSettings && (
                      <div className="tw-flex tw-flex-col tw-gap-3">
                        <div className="tw-flex tw-flex-col tw-gap-1">
                          <Label className="tw-mb-0">Send update notifications</Label>
                          <p className="tw-m-0 tw-text-xs tw-leading-4 tw-text-muted-foreground">
                            Registered guests will receive notifications to add +1 guests
                          </p>
                        </div>

                        <div className="tw-flex tw-flex-col tw-gap-2">
                          <CheckboxField
                            name="sendEmailNotification"
                            label="Email"
                            className="tw-mt-0"
                          />
                          <CheckboxField
                            name="sendSlackNotification"
                            label="Slack"
                            className="tw-mt-0"
                          />
                        </div>
                      </div>
                    )}
                    <div className="tw-flex tw-flex-col tw-gap-2">
                      <InputField
                        name="maxPlusOnes"
                        label="Allowable plus one guests per employee"
                        type="number"
                        min={1}
                        className="tw-mt-0"
                      />
                    </div>

                    <div className="tw-flex tw-flex-col tw-gap-3">
                      <div className="tw-flex tw-flex-col tw-gap-1">
                        <Label className="tw-mb-0">Collect guest information</Label>
                        <p className="tw-m-0 tw-text-xs tw-leading-4 tw-text-muted-foreground">
                          This will be required for each +1 guest added to the event
                        </p>
                      </div>

                      <div className="tw-flex tw-flex-col tw-gap-2">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <Checkbox checked disabled className="tw-mt-0" />
                          <Label className="tw-mb-0">First name</Label>
                        </div>

                        <div className="tw-flex tw-items-center tw-gap-3">
                          <Checkbox checked disabled className="tw-mt-0" />
                          <Label className="tw-mb-0">Last name</Label>
                        </div>

                        <CheckboxField
                          name="collectQuestionnaireResponses"
                          label="Questionnaire responses"
                          description={
                            <div className="tw-flex tw-flex-col tw-gap-1">
                              <span>Collected if there is an event questionnaire</span>
                              {sessionId && dirtyFields.collectQuestionnaireResponses && (
                                <div className="tw-flex tw-items-start tw-gap-1 tw-text-warning-foreground">
                                  <Info className="tw-h-4 tw-w-4" />
                                  <span>
                                    This will apply to all sessions{' '}
                                    {!isPublished && 'when the session is published'}
                                  </span>
                                </div>
                              )}
                              {dirtyFields.collectQuestionnaireResponses &&
                                initialValues.collectQuestionnaireResponses &&
                                !!initialValues.maxRegisteredPlusOnes && (
                                  <div className="tw-flex tw-items-start tw-gap-1 tw-text-negative-foreground">
                                    <Info className="tw-h-4 tw-w-4" />
                                    <span>Collected responses will be removed.</span>
                                  </div>
                                )}
                              {dirtyFields.collectQuestionnaireResponses &&
                                !initialValues.collectQuestionnaireResponses &&
                                !!initialValues.maxRegisteredPlusOnes &&
                                hasRequiredPreEventQuestions && (
                                  <div className="tw-flex tw-items-start tw-gap-1 tw-text-warning-foreground">
                                    <Info className="tw-h-4 tw-w-4" />
                                    <span>
                                      Guests will receive a notification to complete their plus
                                      one’s registration.
                                    </span>
                                  </div>
                                )}
                            </div>
                          }
                          className="tw-mt-0 tw-items-start"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <ModalFooter>
                <Button variant="primary" size="sm" type="submit" className="tw-w-full">
                  Save
                </Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Form>
      </Modal>
    );
  }
);
